import { Box, Grid, Link, Paper, Stack, Typography } from '@mui/material';
import React from 'react';

const projects = [
  {
    title: 'groovDB',
    description:
      'A centralized database of literature-verified transcription factors and their interactions with DNA and small molecules.',
    publication: {
      label: 'Nucleic Acids Research (2025)',
      href: 'https://academic.oup.com/nar/advance-article/doi/10.1093/nar/gkaf1074/8307354',
    },
    repository: {
      label: 'github.com/groov-bio/groov-db-ui',
      href: 'https://github.com/groov-bio/groov-db-ui',
    },
    application: {
      label: 'groov.bio',
      href: 'https://groov.bio',
    },
    image: 'groovDB.png',
    imageAlt: 'groovDB logo',
  },
  {
    title: 'Snowprint',
    description:
      'An inference tool that predicts DNA binding motifs for transcription factors using sequence phylogenies and genome context.',
    publication: {
      label: 'Communications Biology (2024)',
      href: 'https://www.nature.com/articles/s42003-024-05849-8',
    },
    repository: {
      label: 'github.com/simonsnitz/snowprint',
      href: 'https://github.com/simonsnitz/snowprint',
    },
    application: {
      label: 'snowprint.groov.bio',
      href: 'https://snowprint.groov.bio',
    },
    image: 'Snowprint.png',
    imageAlt: 'Snowprint logo',
  },
  {
    title: 'Ligify',
    description:
      'An inference tool that predicts transcription factors responsive to an input chemical using genome context and enzyme reaction data.',
    publication: {
      label: 'ACS Synthetic Biology (2024)',
      href: 'https://pubs.acs.org/doi/10.1021/acssynbio.4c00372',
    },
    repository: {
      label: 'github.com/groov-bio/ligify-ui',
      href: 'https://github.com/groov-bio/ligify-ui',
    },
    application: {
      label: 'ligify.groov.bio',
      href: 'https://ligify.groov.bio',
    },
    image: 'Ligify.png',
    imageAlt: 'Ligify logo',
  },
];

export default function Code() {
  return (
    <Grid container justifyContent="center" mb={8}>
      <Grid size={{ xs: 11, sm: 10, md: 9, lg: 8 }}>
        <Grid
          container
          spacing={{ xs: 4, md: 6 }}
          alignItems="center"
          mt={5}
          justifyContent="space-between"
          sx={{ mb: { xs: 6, md: 8 } }}
        >
          <Grid size={{ xs: 12, md: 5 }} sx={{display: 'flex', justifyContent: "center"}}>
            <Box
              component="img"
              src="Cafe-Coding.jpg"
              alt="code"
              sx={{ width: '100%', maxWidth: { xs: 220, md: 330 }, display: 'block', mx: { xs: 'auto', md: 0 } }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 7 }}>
            <Stack spacing={2.5} sx={{ textAlign: { xs: 'center', md: 'left' } }}>
              {/* <Typography sx={{ fontFamily: 'Inter', fontSize: { xs: 28, md: 34 }, fontWeight: 300 }}>
                Web applications for biosensor development
              </Typography> */}
              <Typography sx={{ fontFamily: 'Inter', fontSize: { xs: 14, sm: 16, md: 18 }, color: 'text.secondary' }}>
                To improve accessibility, I've created several web applications for studying and engineering biosensors.
                User interfaces are built using React and several data visualization libraries. The backend is typically managed 
                by AWS and Cloudflare services. <a href="https://www.linkedin.com/in/joshua-d-love/" target="__blank">Josh Love </a> 
                has been an incredible mentor and contributor on these projects, which are fun hobbies to work on while cafe-hopping.

              </Typography>
              <Stack spacing={0.75} sx={{ alignItems: { xs: 'center', md: 'flex-start' } }}>
                <Typography sx={{ fontFamily: 'Inter', fontSize: { xs: 13, sm: 14 }, fontWeight: 600 }}>
                  Explore more projects
                </Typography>
                <Link
                  href="https://github.com/simonsnitz"
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="hover"
                  sx={{ fontFamily: 'Inter', fontSize: { xs: 13, sm: 14 } }}
                >
                  github.com/simonsnitz
                </Link>
                <Link
                  href="https://github.com/groov-bio"
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="hover"
                  sx={{ fontFamily: 'Inter', fontSize: { xs: 13, sm: 14 } }}
                >
                  github.com/groov-bio
                </Link>
              </Stack>
            </Stack>
          </Grid>
        </Grid>

        <Stack spacing={{ xs: 4.5, md: 5.5 }}>
          {projects.map((project) => (
            <Paper
              key={project.title}
              elevation={0}
              sx={{
                px: { xs: 3, md: 4 },
                py: { xs: 3.5, md: 4 },
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider',
                background: 'linear-gradient(135deg, rgba(248, 250, 252, 0.92), rgba(241, 245, 249, 0.76))',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Grid container spacing={{ xs: 3, sm: 4 }} alignItems="stretch">
                <Grid size={{ xs: 12, sm: 5 }} >
                  <Link
                    href={project.application.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="none"
                    sx={{ display: 'block', height: '100%' }}
                  >
                    <Box
                      component="img"
                      src={project.image}
                      alt={project.imageAlt}

                      sx={{
                        width: {xs:'70%', sm:'90%', md:'70%'},
                        borderRadius: 2,
                        backgroundColor: 'rgba(255, 255, 255, 0.65)',
                        p: 2,
                        boxShadow: 'inset 0 0 0 1px rgba(148, 163, 184, 0.25)',
                        transition: 'transform 0.32s ease, box-shadow 0.32s ease',
                        '&:hover': {
                          transform: 'scale(1.04)',
                          boxShadow: 'inset 0 0 0 2px rgba(37, 99, 235, 0.28)',
                        },
                      }}
                    />
                  </Link>
                </Grid>
                <Grid size={{ xs: 12, sm: 7 }}>
                  <Stack spacing={2.5} height="100%" justifyContent="flex-start">
                    <Typography sx={{ fontFamily: 'Monospace', fontSize: { xs: 24, sm: 28 }, fontWeight: 500 }}>
                    {formatProjectTitle(project.title)}
                    </Typography>
                    <Typography sx={{ fontFamily: 'Inter', fontSize: { xs: 14, sm: 16 }, color: 'text.secondary' }}>
                      {project.description}
                    </Typography>
                    <Stack spacing={1}>
                      <InfoRow heading="Publication" link={project.publication} />
                      <InfoRow heading="GitHub" link={project.repository} />
                      <InfoRow heading="Web app" link={project.application} />
                    </Stack>
                  </Stack>
                </Grid>
              </Grid>
            </Paper>
          ))}
        </Stack>
      </Grid>
    </Grid>
  );
}

function formatProjectTitle(title) {
  if (title === 'groovDB') {
    return (
      <>
        groov
        <Box component="sup" sx={{ fontSize: '0.58em', ml: 0.25 }}>
          DB
        </Box>
      </>
    );
  }

  return title;
}

function InfoRow({ heading, link }) {
  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 0.75, sm: 1.5 }} alignItems="flex-start">
      <Typography
        sx={{
          fontFamily: 'Inter',
          fontSize: { xs: 13, sm: 14 },
          fontWeight: 600,
          color: 'text.primary',
          minWidth: { sm: 96 },
        }}
      >
        {heading}:
      </Typography>
      <Link
        href={link.href}
        target="_blank"
        rel="noopener noreferrer"
        underline="hover"
        sx={{ fontFamily: 'Inter', fontSize: { xs: 13, sm: 14 }, color: 'text.secondary' }}
      >
        {link.label}
      </Link>
    </Stack>
  );
}
